import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { CompanyForm } from './company-form';
import type { Company, CompanyDraft } from '../types/companies.types';

interface CompanyFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided the dialog edits this company; otherwise it creates a new one. */
  company?: Company | null;
  /** Seed the "share with org" checkbox for new companies (e.g. from the org tab). */
  defaultShareWithOrg?: boolean;
  /** Prefill values for create mode (e.g. the name typed in the company picker). */
  defaults?: CompanyDraft;
  /** Hide the "share with org" checkbox (forces a personal company). */
  canShareWithOrg?: boolean;
  /** Fired after a successful create with the new company — used by the picker to auto-select it. */
  onCreated?: (company: Company) => void;
  orgName?: string;
}

/**
 * Standalone dialog wrapper around `CompanyForm` — used from the companies page and from
 * the picker when it is NOT already inside a dialog. Callers that are already in a dialog
 * should render `CompanyForm` inline instead of stacking a second dialog on top.
 */
export function CompanyFormDialog({
  open,
  onOpenChange,
  company,
  defaultShareWithOrg = false,
  defaults,
  canShareWithOrg = true,
  onCreated,
  orgName,
}: CompanyFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = !!company;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        `onInteractOutside` overrides the ui-kit default, which stops propagation on the raw
        DOM event and thereby breaks layers portaled outside the dialog. Cancelling the Radix
        event alone still prevents outside-click dismissal.
      */}
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="max-w-sm md:max-w-xl font-nunito max-h-[85vh] flex flex-col"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {isEdit ? t('ORGCOMPANIES_EDIT_TITLE') : t('ORGCOMPANIES_NEW_TITLE')}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? t('ORGCOMPANIES_EDIT_SUBTITLE') : t('ORGCOMPANIES_NEW_SUBTITLE')}
          </DialogDescription>
        </DialogHeader>

        {/*
          Radix unmounts dialog content after close, so CompanyForm reseeds on each open
          without an explicit guard. The key covers switching target company while open.
        */}
        <CompanyForm
          key={company?.id ?? 'new'}
          company={company}
          defaults={defaults}
          defaultShareWithOrg={defaultShareWithOrg}
          canShareWithOrg={canShareWithOrg}
          orgName={orgName}
          onCreated={onCreated}
          onDone={() => onOpenChange(false)}
          bodyClassName="flex-1 min-h-0 overflow-y-auto pr-1"
          footerClassName="shrink-0"
        />

      </DialogContent>
    </Dialog>
  );
}

export default CompanyFormDialog;
